import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'components/atoms';
import { Text } from 'components/core/Typography';
import ChevronDown from 'components/svg/ChevronDown';
import { Image } from 'components/core/Image';
import check from 'assets/images/landing/homepage/check.svg';

import './NewSortDropDown.scss';

/**
 * Functional wrapper that replaces the former recompose `enhance` HOC.
 * Preserves the same injected props (`isOpen`, `handleClick`, `handleChange`,
 * `closeDropdown`) and the default `paramKey`/`onChange` behavior so existing
 * consumers and tests keep working.
 */
export const enhance = (Wrapped) => {
	function EnhancedNewSortDropDown({
		onChange = () => {},
		paramKey = 'sort',
		...rest
	}) {
		const [isOpen, setIsOpen] = useState(false);

		const handleChange = useCallback(
			(selValue) => {
				setIsOpen(false);
				onChange({ [paramKey]: selValue });
			},
			[onChange, paramKey],
		);

		const handleClick = useCallback(() => {
			setIsOpen((prev) => !prev);
		}, []);

		const closeDropdown = useCallback(() => {
			setIsOpen(false);
		}, []);

		return (
			<Wrapped
				{...rest}
				onChange={onChange}
				paramKey={paramKey}
				isOpen={isOpen}
				setIsOpen={setIsOpen}
				handleChange={handleChange}
				handleClick={handleClick}
				closeDropdown={closeDropdown}
			/>
		);
	}

	EnhancedNewSortDropDown.propTypes = {
		onChange: PropTypes.func,
		paramKey: PropTypes.string,
	};

	return EnhancedNewSortDropDown;
};

export function NewSortDropDown(props) {
	const {
		options,
		value,
		handleChange,
		handleClick,
		isOpen,
		closeDropdown,
		label,
	} = props;
	const containerRef = useRef(null);
	const selectedValue =
		options.find((opt) => opt.value === value) || options[0];
	useEffect(() => {
		function handleClickOutside(event) {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target)
			) {
				closeDropdown();
			}
		}

		function handleCloseOnScrollOrResize() {
			closeDropdown();
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
			window.addEventListener(
				'scroll',
				handleCloseOnScrollOrResize,
				true,
			);
			window.addEventListener('resize', handleCloseOnScrollOrResize);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			window.removeEventListener(
				'scroll',
				handleCloseOnScrollOrResize,
				true,
			);
			window.removeEventListener('resize', handleCloseOnScrollOrResize);
		};
	}, [isOpen]);

	return (
		<div
			className="sort-container"
			ref={containerRef}
		>
			<Text
				className="sort-label"
				size="smallMedium"
				weight="standard"
			>
				{label}
			</Text>
			<Button
				className="selected-sort"
				onClick={handleClick}
				aria-expanded={isOpen}
				aria-haspopup="listbox"
				aria-label="Sort options"
				role="combobox"
				tabIndex={0}
			>
				<Text
					color="priceBlue"
					weight="bold"
					size="smallMedium"
				>
					{selectedValue.label}
				</Text>
				<ChevronDown />
			</Button>
			{isOpen && (
				<ul
					className="sort-options"
					role="listbox"
					aria-label="Sort options list"
				>
					{options.map((option) => (
						<li
							className="option"
							key={option.value}
							role="option"
							aria-selected={selectedValue.value === option.value}
						>
							<Button
								className={
									selectedValue.value === option.value
										? 'option-button active'
										: 'option-button'
								}
								onClick={() => handleChange(option.value)}
							>
								{option.label}
								{selectedValue.value === option.value && (
									<Image
										src={check}
										className="check-icon"
									/>
								)}
							</Button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

NewSortDropDown.defaultProps = {
	label: 'Sort By:',
};

NewSortDropDown.propTypes = {
	label: PropTypes.string,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.any,
			label: PropTypes.any,
		}),
	).isRequired,
	value: PropTypes.string,
	handleChange: PropTypes.func,
	handleClick: PropTypes.func,
	isOpen: PropTypes.bool,
	closeDropdown: PropTypes.func,
	paramKey: PropTypes.string,
};

export default enhance(NewSortDropDown);
